import React from "react";
import { Button, Divider, Typography, Popover, Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ChevronDownIcon as ArrowDropDown } from "lucide-react";
import clsx from "clsx";
import {
  ExerciseTagDto,
  TagDto,
} from "@growth-machine-llc/stridist-api-client";
import ResetFiltersButton from "./ResetFiltersButton";
import { TagsFiltersContent } from "./TagsFiltersContent";

const useStyles = makeStyles((theme) => ({
  root: {
    fontWeight: "bold",
    fontSize: 15,
  },
  active: {
    color: theme.palette.primary.main,
  },
  inactive: {
    color: theme.palette.text.primary,
  },
  paper: {
    borderRadius: theme.spacing(1.5),
    minWidth: theme.spacing(32),
    padding: theme.spacing(2, 0),
    margin: 0,
    overflowY: "hidden",
  },
  headerText: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 500,
  },
  header: {
    padding: theme.spacing(0, 1, 0.5, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

export const NONE_TAG_ID = -1;

interface TagsFiltersProps {
  label?: string;
  selectedTagIds: number[];
  onTagChange: (ids?: number[], allOptionsSelected?: boolean) => void;
  anchorEl?: HTMLElement | null;
  tags?: ExerciseTagDto[] | TagDto[];
  className?: string;
  isClientTags?: boolean;
}

export function TagsFilters({
  selectedTagIds = [],
  label = "Tags",
  onTagChange,
  className,
  tags = [],
}: TagsFiltersProps) {
  const s = useStyles();
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);

  const allTagsWithNone = React.useMemo(
    () => [...tags.map((tag) => tag.id), NONE_TAG_ID],
    [tags],
  );

  const allIsSelected =
    selectedTagIds.length === allTagsWithNone.length &&
    allTagsWithNone.every((id) => selectedTagIds.includes(id));

  React.useEffect(() => {
    if ((!selectedTagIds || selectedTagIds.length === 0) && tags.length > 0) {
      handleAllToggle();
    }
  }, [selectedTagIds, tags]);

  const handleAllToggle = () =>
    onTagChange(
      allIsSelected ? [NONE_TAG_ID] : allTagsWithNone,
      !allIsSelected,
    );

  const tagFilterStatusText = React.useMemo(() => {
    const total = tags.length;
    const selected = selectedTagIds.length;

    return selected !== total + 1 ? `: ${selected}` : "";
  }, [selectedTagIds, tags]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  if (tags.length === 0) return null;

  return (
    <>
      <Button
        ref={buttonRef}
        className={clsx(
          tagFilterStatusText ? s.active : s.inactive,
          s.root,
          className,
        )}
        onClick={handleOpen}
      >
        {label} {tagFilterStatusText}
        <ArrowDropDown size={20} style={{ marginLeft: 2 }} />
      </Button>
      <Popover
        open={open}
        anchorEl={buttonRef.current}
        onClose={handleClose}
        classes={{ paper: s.paper }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box className={s.header}>
          <Typography className={clsx(s.headerText)}>Filter by Tags</Typography>
          <ResetFiltersButton
            tooltipPlacement="bottom"
            disabled={allIsSelected}
            onClick={() => {
              handleAllToggle();
            }}
          />
        </Box>
        <Divider />
        <Box sx={{ overflowY: "auto", maxHeight: 450 }}>
          <TagsFiltersContent
            selectedTagIds={selectedTagIds}
            onTagChange={onTagChange}
            tags={tags}
          />
        </Box>
      </Popover>
    </>
  );
}
